import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, FileSpreadsheet, File, Search, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
const resources = [{
  id: 1,
  title: 'HACCP Plan Template',
  desc: 'Complete, customisable HACCP plan template for food businesses.',
  category: 'HACCP Templates',
  type: 'PDF',
  icon: FileText
}, {
  id: 2,
  title: 'Temperature Monitoring Log',
  desc: 'Daily fridge and freezer temperature recording sheet.',
  category: 'Checklists',
  type: 'PDF',
  icon: FileText
}, {
  id: 3,
  title: 'Cleaning Schedule Template',
  desc: 'Customisable daily, weekly, and monthly cleaning schedule for kitchens.',
  category: 'Checklists',
  type: 'XLS',
  icon: FileSpreadsheet
}, {
  id: 4,
  title: 'Allergen Matrix Template',
  desc: 'Track and display the 14 major allergens across your menu items.',
  category: 'Compliance Documents',
  type: 'XLS',
  icon: FileSpreadsheet
}, {
  id: 5,
  title: 'Food Safety Checklist',
  desc: 'Comprehensive daily opening and closing food safety checklist.',
  category: 'Checklists',
  type: 'PDF',
  icon: FileText
}, {
  id: 6,
  title: 'Supplier Approval Form',
  desc: 'Template for assessing and approving new food suppliers.',
  category: 'Compliance Documents',
  type: 'PDF',
  icon: FileText
}, {
  id: 7,
  title: 'Staff Training Record',
  desc: 'Track employee training completion, dates, and renewal requirements.',
  category: 'Compliance Documents',
  type: 'PDF',
  icon: FileText
}, {
  id: 8,
  title: 'Pest Control Log',
  desc: 'Record pest control inspections, sightings, and corrective actions.',
  category: 'Compliance Documents',
  type: 'PDF',
  icon: FileText
}, {
  id: 9,
  title: 'Food Safety Policy Template',
  desc: 'Customisable food safety policy document for your staff handbook.',
  category: 'Compliance Documents',
  type: 'DOCX',
  icon: File
}, {
  id: 10,
  title: 'Kitchen Hygiene Poster',
  desc: 'Printable hand washing and general hygiene rules poster for staff areas.',
  category: 'Posters',
  type: 'PDF',
  icon: FileText
}];
const categories = ['All', 'HACCP Templates', 'Checklists', 'Compliance Documents', 'Posters'];
export function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredResources = resources.filter(r => {
    const matchesCategory = activeCategory === 'All' || r.category === activeCategory;
    const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="min-h-screen bg-cream-50 pt-20 pb-24" data-id="element-949">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 text-white overflow-hidden py-24 mb-16" data-id="element-950">
        <div className="absolute inset-0 bg-mesh opacity-30" data-id="element-951"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ember-500/20 rounded-full blur-3xl" data-id="element-952"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-953">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-954">
            <Badge variant="gradient" className="mb-6" data-id="element-955">
              <Download className="w-3 h-3 mr-1" data-id="element-956" /> Free Downloads
            </Badge>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-957">
              Templates & <span className="text-ember-400" data-id="element-958">Resources</span>
            </h1>
            <p className="text-xl text-forest-100 max-w-2xl mx-auto leading-relaxed" data-id="element-959">
              Free food safety templates, checklists, and compliance documents
              to help you maintain the highest standards in your business.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-960">
        {/* Filters & Search */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12" data-id="element-961">
          <div className="flex flex-wrap gap-3" data-id="element-962">
            {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${activeCategory === cat ? 'bg-forest-700 text-white shadow-md' : 'bg-white text-gray-600 border-2 border-forest-100 hover:border-forest-300'}`} data-id="element-963">
                {cat}
              </button>)}
          </div>

          <div className="relative w-full lg:w-80" data-id="element-964">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-965" />
            <input type="text" placeholder="Search resources..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-forest-100 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-sm font-medium bg-white shadow-sm" data-id="element-966" />
          </div>
        </div>

        {/* Resource Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-967">
          {filteredResources.map((resource, i) => <motion.div key={resource.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} className="bg-white rounded-3xl p-6 border-2 border-forest-100 hover:border-ember-300 hover:shadow-xl transition-all group flex flex-col h-full" data-id="element-968">
              <div className="flex justify-between items-start mb-6" data-id="element-969">
                <div className="w-12 h-12 bg-forest-50 rounded-xl flex items-center justify-center group-hover:bg-ember-50 transition-colors" data-id="element-970">
                  <resource.icon className="w-6 h-6 text-forest-600 group-hover:text-ember-500 transition-colors" data-id="element-971" />
                </div>
                <div className="flex flex-col items-end gap-2" data-id="element-972">
                  <Badge variant="outline" className="text-[10px]" data-id="element-973">
                    {resource.category}
                  </Badge>
                  <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-md" data-id="element-974">
                    {resource.type}
                  </span>
                </div>
              </div>

              <h3 className="text-xl font-display font-bold text-forest-900 mb-3 group-hover:text-forest-700 transition-colors" data-id="element-975">
                {resource.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-8 flex-grow" data-id="element-976">
                {resource.desc}
              </p>

              <Button variant="outline" className="w-full group-hover:bg-forest-700 group-hover:text-white group-hover:border-forest-700 transition-all" data-id="element-977">
                <Download className="w-4 h-4 mr-2" data-id="element-978" /> Download Free
              </Button>
            </motion.div>)}
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-forest-50 rounded-3xl p-10 text-center border-2 border-forest-100" data-id="element-979">
          <h2 className="text-2xl font-display font-bold text-forest-900 mb-4" data-id="element-980">
            Need custom templates for your business?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-981">
            Our food safety consultants can develop bespoke HACCP plans and
            compliance documentation tailored specifically to your operations.
          </p>
          <Link to="/contact" data-id="element-982">
            <Button variant="primary" data-id="element-983">
              Contact Our Consultants <ArrowRight className="w-4 h-4 ml-2" data-id="element-984" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>;
}